import { DialogProps, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { ConfirmOptions, useConfirm } from 'material-ui-confirm';
import { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import { DRAWER_Z_INDEX } from '../../definitions/constants';
import { isVendorMode } from '../Vendor';

const useStyles = makeStyles((theme: Theme) => ({
  capitalized: {
    textTransform: 'capitalize',
  },
  hideConfirmationButton: {
    display: 'none',
  },
  useErrorColor: {
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  useSecondaryColor: {
    backgroundColor: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
}));

interface ButtonPropsWithTestId extends MuiButtonProps {
  'data-testid'?: string;
}

interface DialogPropsWithTestId extends DialogProps {
  'data-testid'?: string;
}

type PickedConfirmProps = Omit<ConfirmOptions, 'dialogProps'>;
type PickedDialogProps = Pick<
  DialogProps,
  'classes' | 'disablePortal' | 'maxWidth' | 'style'
>;

export interface ShowConfirmParams
  extends PickedConfirmProps,
    PickedDialogProps {
  hideConfirmationButton?: boolean;
  useErrorColor?: boolean;
  cancelDataTestId?: string;
  confirmDataTestId?: string;
  modalDataTestId?: string;
}

/**
 * Convenience hook that wraps useConfirm to display the confirmation
 * dialog with certain customized and re-used configurations, so this
 * becomes easier to implement with fewer lines.
 *
 * Use the 'useErrorColor' prop in order to give the confirmation
 * button an error color in scenarios when the confirmation performs
 * an action that cannot be undone (e.g.: deleting a record).
 *
 * Usage:
 *  1. As this is a hook that doesn't depend on the component's state,
 *     put the following line at the top of the component's body,
 *     even before the component's props are destructured:
 *
 *     const MyComponent: React.FC<Props> = (props: Props): JSX.Element => {
 *       const showConfirm = useShowConfirm();
 *
 *       const { foo, bar } = props;
 *     };
 *
 *  2. Invoke the 'showConfirm' function whenever you need to display the
 *     confirmation dialog. Pass the disablePortal, maxWidth or style props
 *     directly, instead of modifying the dialog props directly:
 *
 *     const MyComponent: React.FC<Props> = (props: Props): JSX.Element => {
 *       const showConfirm = useShowConfirm();
 *
 *       const { foo, bar } = props;
 *
 *       const confirmSomething = () => {
 *         showConfirm({
 *           confirmationText: 'Confirm',
 *           maxWidth: 'md',
 *           title: 'Confirm something',
 *           useErrorColor: true,
 *           description: (
 *             <Typography color="error" variant="caption">Foo...</Typography>
 *           ),
 *         }).then(
 *           () => handleSomething(),
 *           // do nothing as this is only catching the cancellation
 *           () => {}
 *         );
 *       };
 *     };
 */
export const useShowConfirm = () => {
  const classes = useStyles();
  const confirm = useConfirm();

  return (params: ShowConfirmParams): Promise<void> => {
    const {
      allowClose = true,
      cancellationText = 'Cancel',
      classes: classesProp,
      confirmationButtonProps,
      confirmationText,
      content,
      contentProps,
      description,
      disablePortal = false,
      hideConfirmationButton = false,
      maxWidth = 'xs',
      style,
      title,
      useErrorColor = false,
      cancelDataTestId = 'cancel-button',
      confirmDataTestId = 'confirm-button',
      modalDataTestId = 'confirm-modal',
    } = params;

    const capitalize = isVendorMode();

    return confirm({
      allowClose,
      cancellationButtonProps: {
        color: 'inherit',
        classes: {
          root: classNames({
            [classes.capitalized]: capitalize,
          }),
        },
        'data-testid': cancelDataTestId,
      } as ButtonPropsWithTestId,
      cancellationText,
      confirmationText,
      confirmationButtonProps: {
        color: 'secondary',
        classes: {
          ...confirmationButtonProps?.classes,
          root: classNames({
            [classes.capitalized]: capitalize,
            [classes.useErrorColor]: useErrorColor,
            [classes.useSecondaryColor]: !useErrorColor,
            [classes.hideConfirmationButton]: hideConfirmationButton,
          }),
        },
        variant: 'contained',
        'data-testid': confirmDataTestId,
        ...confirmationButtonProps,
      } as ButtonPropsWithTestId,
      content,
      contentProps,
      description,
      dialogProps: {
        classes: classesProp,
        disablePortal,
        maxWidth,
        style: {
          zIndex: DRAWER_Z_INDEX + 1,
          ...style,
        },
        'data-testid': modalDataTestId,
      } as DialogPropsWithTestId,
      title,
    });
  };
};
